import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS, homePathFor } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { AuthCard } from "@/components/auth/AuthCard";
import { TwoFactorStep } from "@/components/auth/TwoFactorStep";

const InviteForm = ({ token, info, onChallenge }) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/invite/accept", { token, name, password });
      onChallenge(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const role = ROLE_LABELS[info.platform_role || info.org_role];
  return (
    <form onSubmit={submit} data-testid="invite-form">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent text-white"><MailOpen className="h-5 w-5" /></span>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">Accept your invite</h1>
      <p className="mt-2 text-sm text-oath-secondary" data-testid="invite-summary">
        You're joining {info.org_name ? <b>{info.org_name}</b> : <b>ResidenceProof staff</b>} as <b>{role}</b>.
      </p>
      <label className="mt-6 block font-mono-label text-[10px] font-semibold text-oath-secondary">EMAIL</label>
      <Input value={info.email} disabled className="mt-2" data-testid="invite-email" />
      <label className="mt-4 block font-mono-label text-[10px] font-semibold text-oath-secondary">FULL NAME</label>
      <Input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="mt-2" data-testid="invite-name-input" />
      <label className="mt-4 block font-mono-label text-[10px] font-semibold text-oath-secondary">PASSWORD (MIN 10 CHARACTERS)</label>
      <Input type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2" autoComplete="new-password" data-testid="invite-password-input" />
      <label className="mt-4 block font-mono-label text-[10px] font-semibold text-oath-secondary">CONFIRM PASSWORD</label>
      <Input type="password" required minLength={10} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2" autoComplete="new-password" data-testid="invite-confirm-input" />
      <button type="submit" disabled={loading} data-testid="invite-submit-button"
        className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white hover:bg-black disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "CREATE ACCOUNT"}
      </button>
    </form>
  );
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  const done = (u) => {
    setUser(u);
    navigate(homePathFor(u), { replace: true });
  };

  return (
    <AuthCard testid="invite-panel">
      {error ? (
        <div data-testid="invite-error">
          <h1 className="font-display text-2xl font-bold text-oath-ink">Invite unavailable</h1>
          <p className="mt-3 text-sm text-oath-secondary">{error}</p>
          <Link to="/signin" className="mt-6 inline-block text-sm text-oath-accent underline">Go to sign in</Link>
        </div>
      ) : challenge ? (
        <TwoFactorStep challenge={challenge} onDone={done} />
      ) : info ? (
        <InviteForm token={token} info={info} onChallenge={setChallenge} />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-oath-muted" />
      )}
    </AuthCard>
  );
}
